import { useState, useEffect } from 'react'
import { Link } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface BannerProps {
  content: string
  cta?: {
    label: string
    href: string
  }
  dismissible?: boolean
  variant?: 'info' | 'success' | 'warning'
  className?: string
  id?: string // Identifiant unique pour persister l'état de fermeture
}

const variantStyles = {
  info: 'bg-tessan-yellow text-tessan-green',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
}

const DISMISSED_BANNERS_KEY = 'tessan_dismissed_banners'

function getDismissedBanners(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveDismissedBanner(id: string): void {
  if (typeof window === 'undefined') return
  try {
    const dismissed = getDismissedBanners()
    if (!dismissed.includes(id)) {
      dismissed.push(id)
      localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(dismissed))
    }
  } catch {
    // Ignore localStorage errors
  }
}

export function Banner({
  content,
  cta,
  dismissible = true,
  variant = 'info',
  className = '',
  id,
}: BannerProps) {
  const [visible, setVisible] = useState(true)

  // Vérifie au montage si le banner a déjà été fermé
  useEffect(() => {
    if (id && getDismissedBanners().includes(id)) {
      setVisible(false)
    }
  }, [id])

  const handleDismiss = () => {
    setVisible(false)
    if (id) {
      saveDismissedBanner(id)
    }
  }

  if (!visible) return null

  return (
    <div className={`${variantStyles[variant]} py-3 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <p className="text-sm flex-1 font-medium">{content}</p>

        <div className="flex items-center gap-3">
          {cta && (
            <Link href={cta.href}>
              <Button variant="outline" className="text-sm py-1 px-3">
                {cta.label}
              </Button>
            </Link>
          )}

          {dismissible && (
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-black/10 rounded transition-colors"
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
